import {
  MotifButton,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
  MotifIcon,
  MotifFormField,
  MotifErrorMessage,
} from "@ey-xd/motif-react";
import "./CTAssignProxy.scss";
import { IconoirTrash } from "@ey-xd/motif-icon";
import { useEffect, useId, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  searchUsers,
  resetSearchUsers,
} from "../../store/slices/CTDBUserListSlice";
import { RootState, AppDispatch } from "../../store";
import SearchInput from "../SearchInput/SearchInput";
import { containsEmoji } from "../../utils/common";
import { EMOJI_ERROR_MESSAGE } from "../../Constant";

interface ProxyOwner {
  Name: string;
  Email: string;
}

interface CTAssignProxyProps {
  show: boolean;
  proxyOwners: ProxyOwner[];
  onClose: () => void;
  onAssign: (proxyOwnersList: ProxyOwner[]) => void;
}

const CTAssignProxy: React.FC<CTAssignProxyProps> = ({
  show,
  proxyOwners,
  onClose,
  onAssign,
}) => {
  const [userSearch, setUserSearch] = useState("");
  const [searchCharCount, setSearchCharCount] = useState(0);
  const [proxyOwnersList, setProxyOwnersList] = useState<ProxyOwner[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const searchAbortRef = useRef<{ abort: () => void } | null>(null);
  const [searchValidationError, setSearchValidationError] =
    useState<string>("");

  useEffect(() => {
    setProxyOwnersList(proxyOwners);
  }, [proxyOwners]);

  useEffect(() => {
    return () => {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      dispatch(resetSearchUsers());
    };
  }, [dispatch]);

  const userList = useSelector(
    (state: RootState) => state.CTDBUserListSlice.data,
  );
  const userListLoading = useSelector(
    (state: RootState) => state.CTDBUserListSlice.isLoading,
  );

  const handleDeleteClick = (email: string) => () => {
    const updatedList = proxyOwnersList.filter(
      (owner) => owner.Email !== email,
    );
    setProxyOwnersList(updatedList);
  };

  const handleCancelClick = () => {
    setUserSearch("");
    onClose();
  };

  const handleAddToList = () => {
    onAssign(proxyOwnersList);
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputValue = event.target.value;
    setUserSearch(inputValue);
    setSearchCharCount(inputValue?.length);
    if (inputValue.trim().length >= 3) {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      dispatch(resetSearchUsers());
      searchAbortRef.current = dispatch(
        searchUsers({ searchText: inputValue.trim() }),
      ) as unknown as { abort: () => void };
    }
  };

  const handleUserClick = (userData: string) => {
    if (!userData) return;
    const newProxyOwner = {
      Name: userData.split("(")[0].trim(),
      Email: userData.split("(")[1].split(")")[0].trim(),
    };
    setProxyOwnersList([...proxyOwnersList, newProxyOwner]);
    setUserSearch("");
    dispatch(resetSearchUsers());
  };

  const filteredItems = Array.isArray(userList)
    ? userList.filter((userData: any) => {
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
        return emailRegex.test(userData.email);
      })
    : [];

  const displayItems = filteredItems.map(
    (userData: any) => `${userData.displayName} (${userData.email})`,
  );
  return (
    <MotifModal
      show={show}
      onClose={onClose}
      className="ct-assign-proxy-owner"
      size="lg"
    >
      <MotifModalHeader>Assign Commercial Proxy Owner</MotifModalHeader>
      <MotifModalBody>
        <p className="assign-proxy-txt">Assign your commercial proxy owner</p>

        <MotifFormField>
          <SearchInput
            aria-label="Search for a user"
            id={`user-search-assign-proxy-${useId()}`}
            placeholder="Enter a user's name or Email ID"
            open={!userListLoading}
            className={`assign-proxy-search-input ${
              searchCharCount >= 3 ? "show-suggestions" : ""
            }`}
            uniqueClass="assign-proxy-search-input"
            items={
              userList &&
              userList
                .filter((userData: any) => {
                  const emailRegex =
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
                  return emailRegex.test(userData.email);
                })
                .filter((userData: any) => {
                  return (
                    proxyOwnersList.filter(
                      (owner) => owner.Email == userData.email,
                    ).length == 0
                  );
                })
                .map((userData: any) => {
                  return userData.displayName + " (" + userData.email + ")";
                })
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const isContainEmoji = containsEmoji(e.target.value);
              if (isContainEmoji) {
                setSearchValidationError(EMOJI_ERROR_MESSAGE);
              } else {
                setSearchValidationError("");
                handleSearchInputChange(e);
              }

              // if (e.target.value === "") {
              //   handleSearchInputChange(e)
              // }
            }}
            onSelect={(item: any) => handleUserClick(item)}
            visibleOptions={3}
            value={userSearch}
          />
          {userSearch.trim().length > 3 &&
            !userListLoading &&
            userList &&
            !displayItems.length && (
              <MotifErrorMessage
                aria-live="assertive"
                role="alert"
                id="aria-error-primaryList"
              >
                No Results Found
              </MotifErrorMessage>
            )}
        </MotifFormField>
        <div>
          {searchValidationError && (
            <MotifErrorMessage className="search-helper-text">
              {searchValidationError}
            </MotifErrorMessage>
          )}
        </div>
        <section>
          <h4>Assign Commercial Proxy Owner(s)</h4>
          <div className="ct-assign-proxy-owner-table">
            <div className="ct-assign-proxy-owner-table-header">
              <div
                className="ct-assign-proxy-owner-table-header-cell"
                style={{ width: "103px" }}
              >
                Serial No.
              </div>
              <div
                className="ct-assign-proxy-owner-table-header-cell"
                style={{ width: "262px" }}
              >
                Employee Name
              </div>
              <div
                className="ct-assign-proxy-owner-table-header-cell"
                style={{ width: "661px" }}
              >
                Email
              </div>
              <div
                className="ct-assign-proxy-owner-table-header-cell"
                style={{ width: "166px" }}
              >
                Action
              </div>
            </div>
            {proxyOwnersList.map((proxyOwner, index) => (
              <div
                className="ct-assign-proxy-owner-table-row"
                key={proxyOwner.Email}
              >
                <div
                  className="ct-assign-proxy-owner-table-cell"
                  style={{ width: "103px" }}
                >
                  {index + 1}.
                </div>
                <div
                  className="ct-assign-proxy-owner-table-cell"
                  style={{ width: "262px" }}
                >
                  {proxyOwner.Name}
                </div>
                <div
                  className="ct-assign-proxy-owner-table-cell"
                  style={{ width: "661px" }}
                >
                  {proxyOwner.Email}
                </div>
                <div
                  className="ct-assign-proxy-owner-table-cell"
                  style={{ width: "166px" }}
                >
                  <MotifButton
                    className="ct-assign-proxy-owner-table-btn"
                    onClick={handleDeleteClick(proxyOwner.Email)}
                  >
                    <MotifIcon
                      iconFunction={IconoirTrash}
                      size="24"
                      title="Remove from list"
                    />
                  </MotifButton>
                </div>
              </div>
            ))}
          </div>
        </section>
      </MotifModalBody>
      <MotifModalFooter>
        <MotifButton
          onClick={handleAddToList}
          className="ct-assign-product-add"
          type="button"
        >
          Update List
        </MotifButton>
        <MotifButton
          onClick={handleCancelClick}
          className="ct-assign-product-cancel"
          type="button"
        >
          Cancel
        </MotifButton>
      </MotifModalFooter>
    </MotifModal>
  );
};

export default CTAssignProxy;
